import { CheckoutProgress } from "@/components/checkout/CheckoutProgress";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { type OrderConfirmation, useOrderConfirmation } from "@/queries/orders";
import { convertToLocale } from "@/utils/money";
import type { HttpTypes } from "@medusajs/types";
import { cn } from "@repo/design-system/utils";
import { createRoute, Link, useSearch } from "@tanstack/react-router";
import { CheckCircle, MapPin, Package, Truck } from "lucide-react";
import { memo, useMemo } from "react";
import { z } from "zod";
import { layoutRoute } from "../$layoutRoute";

// ---- Helpers ----

/**
 * Display label for an order. Prefer the custom display id (e.g. "SS-26543646")
 * generated by the backend ORDER module; fall back to the autoincrement
 * `display_id` (e.g. "#1") when it isn't set.
 */
function formatOrderNumber(order: {
  custom_display_id?: string | null;
  display_id?: number | null;
}): string {
  return order.custom_display_id ?? `#${order.display_id}`;
}

// ---- Route ----

const orderConfirmationSearchSchema = z.object({
  order_ids: z.string().optional()
});

export const orderConfirmationRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "order-confirmation",
  component: OrderConfirmationPage,
  validateSearch: orderConfirmationSearchSchema
});

// ---- Page ----

function OrderConfirmationPage() {
  const { order_ids } = useSearch({ from: orderConfirmationRoute.id });

  const orderIds = useMemo(() => {
    if (!order_ids) return [];
    return order_ids.split(",").filter(Boolean);
  }, [order_ids]);

  if (orderIds.length === 0) {
    return <NoOrdersPlaceholder />;
  }

  return <OrderConfirmationView orderIds={orderIds} />;
}

// ---- Confirmation view ----

const OrderConfirmationView = memo(function OrderConfirmationView({
  orderIds
}: {
  orderIds: string[];
}) {
  const { data, isLoading, error } = useOrderConfirmation(orderIds);

  const orders = data?.orders;

  const aggregateTotals = useMemo(() => {
    if (!orders?.length) {
      return {
        subtotal: 0,
        shippingTotal: 0,
        discountTotal: 0,
        total: 0,
        currencyCode: "AUD"
      };
    }
    return {
      subtotal: orders.reduce((sum, o) => sum + (o.item_subtotal ?? 0), 0),
      shippingTotal: orders.reduce(
        (sum, o) => sum + (o.shipping_total ?? 0),
        0
      ),
      discountTotal: orders.reduce(
        (sum, o) => sum + (o.discount_total ?? 0),
        0
      ),
      total: orders.reduce((sum, o) => sum + (o.total ?? 0), 0),
      currencyCode: orders[0].currency_code
    };
  }, [orders]);

  if (isLoading) return <OrderConfirmationSkeleton />;
  if (error) return <OrderConfirmationError error={error} />;
  // Nothing came back: not an exception, so nothing to report.
  if (!orders?.length) return <OrderConfirmationError />;

  const shippingAddress = orders[0]?.shipping_address;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-[900px] px-4 py-8">
        <CheckoutProgress currentStep="complete" />

        <SuccessHeader
          orderCount={orders.length}
          orderNumbers={orders.map(formatOrderNumber)}
          createdAt={orders[0].created_at}
          email={orders[0].email}
        />

        {orders.map((order) => (
          <OrderCard key={order.id} order={order} />
        ))}

        {shippingAddress && <ShippingAddressCard address={shippingAddress} />}

        <OrderTotalsCard totals={aggregateTotals} orderCount={orders.length} />

        <div className="mt-8 text-center">
          <Link
            to="/search"
            className={cn(
              "inline-flex items-center justify-center gap-2",
              "rounded-lg bg-purple-600 px-8 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            )}
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
});

// ---- Success header ----

const SuccessHeader = memo(function SuccessHeader({
  orderCount,
  orderNumbers,
  createdAt,
  email
}: {
  orderCount: number;
  orderNumbers: string[];
  createdAt: string | Date;
  email: string | null;
}) {
  const formattedDate = new Intl.DateTimeFormat("en-AU", {
    dateStyle: "long",
    timeStyle: "short"
  }).format(new Date(createdAt));

  return (
    <div className="mb-8 mt-6 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <CheckCircle className="h-10 w-10 text-green-600" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900">
        Thank you for your order!
      </h1>
      <p className="mt-2 text-gray-600">
        {orderCount > 1
          ? "Your orders have been sent to your suppliers."
          : "Your order has been sent to your supplier."}
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {orderNumbers.map((number) => (
          <span
            key={number}
            className="rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-700"
          >
            Order {number}
          </span>
        ))}
      </div>
      <p className="mt-2 text-sm text-gray-500">
        {formattedDate}
        {email && (
          <>
            {" "}
            &middot; Confirmation sent to{" "}
            {/* The salon's email address (ADR-0006). Masked on the span rather
                than the paragraph so the order date beside it stays readable -
                the date is what dates a replay. */}
            <span className={`font-medium ${PII_MASK_CLASS}`}>{email}</span>
          </>
        )}
      </p>
    </div>
  );
});

// ---- Order card ----

const OrderCard = memo(function OrderCard({
  order
}: {
  order: OrderConfirmation;
}) {
  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Order {formatOrderNumber(order)}
          </h2>
        </div>
        <span className="text-sm font-medium text-gray-500">
          {convertToLocale({
            amount: order.total,
            currency_code: order.currency_code
          })}
        </span>
      </div>

      {order.shipping_methods && order.shipping_methods.length > 0 && (
        <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-3 text-sm text-gray-600">
          <Truck className="h-4 w-4" />
          <span>{order.shipping_methods[0].name}</span>
          <span className="text-gray-400">&middot;</span>
          <span>
            {order.shipping_methods[0].total === 0
              ? "Free"
              : convertToLocale({
                  amount: order.shipping_methods[0].total,
                  currency_code: order.currency_code
                })}
          </span>
        </div>
      )}

      <div className="divide-y divide-gray-100 px-6">
        {order.items?.map((item) => (
          <OrderLineItem
            key={item.id}
            item={item}
            currencyCode={order.currency_code}
          />
        ))}
      </div>
    </section>
  );
});

// ---- Line item ----

const OrderLineItem = memo(function OrderLineItem({
  item,
  currencyCode
}: {
  item: HttpTypes.StoreOrderLineItem;
  currencyCode: string;
}) {
  return (
    <div className="flex gap-4 py-4">
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        <ProductThumbnailImage
          src={item.thumbnail ?? null}
          alt=""
          className="h-full w-full object-cover"
          fallback={
            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
              No image
            </div>
          }
        />
      </div>

      <div className="flex min-w-0 flex-1 justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
          {item.subtitle && (
            <p className="text-xs text-gray-500">{item.subtitle}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Qty: {item.quantity}
            {item.quantity > 1 && (
              <>
                {" "}
                &middot;{" "}
                {convertToLocale({
                  amount: item.unit_price,
                  currency_code: currencyCode
                })}{" "}
                each
              </>
            )}
          </p>
        </div>
        <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
          {convertToLocale({
            amount: item.total,
            currency_code: currencyCode
          })}
        </span>
      </div>
    </div>
  );
});

// ---- Shipping address card ----

const ShippingAddressCard = memo(function ShippingAddressCard({
  address
}: {
  address: NonNullable<HttpTypes.StoreOrder["shipping_address"]>;
}) {
  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
        <MapPin className="h-5 w-5 text-purple-600" />
        <h2 className="text-lg font-semibold text-gray-900">
          Shipping Address
        </h2>
      </div>
      {/* The salon's delivery address - masked out of replays (ADR-0006). */}
      <div className={`px-6 py-4 text-sm text-gray-700 ${PII_MASK_CLASS}`}>
        <p className="font-medium">
          {address.first_name} {address.last_name}
        </p>
        {address.company && <p>{address.company}</p>}
        <p>{address.address_1}</p>
        {address.address_2 && <p>{address.address_2}</p>}
        <p>
          {address.city}
          {address.province && `, ${address.province}`} {address.postal_code}
        </p>
        <p className="uppercase">{address.country_code}</p>
        {address.phone && <p className="mt-1">{address.phone}</p>}
      </div>
    </section>
  );
});

// ---- Order totals card ----

const OrderTotalsCard = memo(function OrderTotalsCard({
  totals,
  orderCount
}: {
  totals: {
    subtotal: number;
    shippingTotal: number;
    discountTotal: number;
    total: number;
    currencyCode: string;
  };
  orderCount: number;
}) {
  const fmt = (amount: number) =>
    convertToLocale({ amount, currency_code: totals.currencyCode });

  // Product prices already include Australian GST; derive the GST component
  // from the GST-inclusive total rather than the backend tax_total, which is
  // 0 unless a tax region is configured. total = ex-GST * 1.1, so GST = total / 11.
  const gstAmount = totals.total * (0.1 / 1.1);

  return (
    <section className="mb-4 rounded-xl border border-gray-200 bg-white">
      <div className="px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">Order Summary</h2>
        {orderCount > 1 && (
          <p className="text-xs text-gray-500">
            Combined total across {orderCount} orders
          </p>
        )}
      </div>
      <div className="space-y-3 border-t border-gray-200 px-6 py-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span className="font-medium text-gray-900">
            {fmt(totals.subtotal)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Shipping</span>
          <span className="font-medium text-gray-900">
            {totals.shippingTotal === 0 ? "Free" : fmt(totals.shippingTotal)}
          </span>
        </div>
        {totals.discountTotal > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Discount</span>
            <span className="font-medium text-green-600">
              -{fmt(totals.discountTotal)}
            </span>
          </div>
        )}
        <div className="border-t border-gray-200 pt-3">
          <div className="rounded-lg bg-purple-50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-2xl font-bold text-gray-900">
                {fmt(totals.total)}
              </span>
            </div>
            {totals.total > 0 && (
              <p className="mt-1 text-right text-sm text-gray-500">
                Includes GST of {fmt(gstAmount)}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

// ---- Loading skeleton ----

const OrderConfirmationSkeleton = memo(function OrderConfirmationSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-[900px] px-4 py-8">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-24" />
        </div>

        <div className="mb-8 mt-6 flex flex-col items-center">
          <Skeleton className="h-16 w-16 rounded-full" />
          <Skeleton className="mt-4 h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-48" />
          <Skeleton className="mt-3 h-6 w-32 rounded-full" />
        </div>

        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-6">
          <Skeleton className="mb-4 h-6 w-32" />
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-16 w-16 rounded-lg" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="mt-2 h-3 w-24" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-6">
          <Skeleton className="mb-3 h-6 w-40" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="mt-1 h-4 w-36" />
          <Skeleton className="mt-1 h-4 w-32" />
        </div>

        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-6">
          <Skeleton className="mb-4 h-6 w-32" />
          <div className="space-y-2">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="flex justify-between pt-2">
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ---- Error state ----

const OrderConfirmationError = memo(function OrderConfirmationError({
  error
}: {
  /** The load failure to report; omit for an empty result, which is not a fault. */
  error?: Error | null;
}) {
  // Reported, not shown: the server's message means nothing to a salon and can
  // leak internals.
  useReportLoadFailure(error, "order confirmation page");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-2xl px-4 py-16">
        <div
          className="rounded-xl border border-red-200 bg-white p-12 text-center shadow-sm"
          role="alert"
        >
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <span className="text-2xl text-red-600" aria-hidden="true">
              !
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            Could not load order details
          </h1>
          <p className="mb-8 text-gray-600">
            We had trouble retrieving your order information. Please try again.
          </p>
          <Link
            to="/"
            className={cn(
              "inline-flex items-center justify-center gap-2",
              "rounded-lg bg-purple-600 px-6 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            )}
          >
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
});

// ---- Empty state ----

const NoOrdersPlaceholder = memo(function NoOrdersPlaceholder() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100">
            <Package className="h-8 w-8 text-purple-600" aria-hidden="true" />
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Your Orders</h1>
          <p className="mb-8 text-gray-600">
            Your order history will appear here. Start shopping to place your
            first order.
          </p>
          <Link
            to="/search"
            className={cn(
              "inline-flex items-center justify-center gap-2",
              "rounded-lg bg-purple-600 px-6 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            )}
          >
            Browse Products
          </Link>
        </div>
      </div>
    </div>
  );
});
